import { useParams } from 'react-router-dom';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { functions } from '../../lib/firebase';
import { useAnonymousAuth } from '../auth';
import { DistrictContextProvider } from './DistrictContext';
import { DistrictAccessGuardContext } from './districtAccessGuard';
import { useDistrictManifest } from './useDistrictManifest';
import { useDocumentTitle } from './useDocumentTitle';
import { useDistrictResolver } from './useDistrictResolver';

/**
 * `/m/:urlToken` route で wrap される Provider。
 * 認証 + resolver を起動し、結果を DistrictContext に渡す。
 *
 * Sprint AP (UI5 H8): district 解決後に dynamic manifest を `<link rel="manifest">` に注入
 * (`useDistrictManifest`)。iOS Safari / Android Chrome の「ホーム画面に追加」前に district 別
 * name / id が見える状態を作り、複数地区の独立 install を可能にする。
 */
export function DistrictProvider({ children }: { children: ReactNode }) {
  const { urlToken } = useParams();
  const authState = useAnonymousAuth();
  const user = authState.status === 'authenticated' ? authState.user : null;
  const state = useDistrictResolver({ urlToken, user, functions });
  useDistrictManifest(state.status === 'ready' ? state.district : null);
  // 要望: ブラウザのタブ名 (サイト名称) を地区名にする。解決前は静的 <title> のまま (issue #16 と同方針)。
  useDocumentTitle(state.status === 'ready' ? state.district.districtName : null);

  // 地区「強い停止」Phase 1: キャッシュ済みの一般ユーザーが、停止後に投稿等を読めず
  // permission-denied になった場合の救済。MapPage / ListPanel が useReportAccessDeniedOnError で
  // 通知し、DistrictGate (App.tsx) がこの accessDenied を見て「現在非公開中」画面へ切り替える。
  // urlToken / user が変わる (別地区へ / ログイン後の再解決) ときはリセットする。
  const [accessDenied, setAccessDenied] = useState(false);
  useEffect(() => {
    setAccessDenied(false);
  }, [urlToken, user]);
  const reportAccessDenied = useCallback(() => setAccessDenied(true), []);

  return (
    <DistrictContextProvider value={state}>
      <DistrictAccessGuardContext.Provider value={{ accessDenied, reportAccessDenied }}>
        {children}
      </DistrictAccessGuardContext.Provider>
    </DistrictContextProvider>
  );
}
